import { useState, useEffect, useCallback } from "react";
import { updateDoctor, createDoctor, deleteDoctor as apiDeleteDoctor, CMSDoctorCreate, CMSDoctorUpdate } from "@/api/doctors";
import { useDataContext, Doctor } from "@/context/DataContext";

export type { Doctor };

// ─── Public hook (for public-facing pages) ────────────────────────────────────
export function usePublicDoctors() {
  const { doctors, doctorsLoading: loading, reloadDoctors } = useDataContext();
  
  useEffect(() => {
    reloadDoctors();
  }, [reloadDoctors]);

  return { doctors, loading };
}

// ─── CMS Admin hook (for CMS admin pages) ────────────────────────────────────
export function useCMSDoctors() {
  const {
    doctors: globalDoctors,
    doctorsLoading: loading,
    doctorsError: error,
    reloadDoctors: reload,
  } = useDataContext();

  const [doctors, setDoctors] = useState<Doctor[]>(globalDoctors);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [hasChanges, setHasChanges] = useState(false);

  // Keep local editor state in sync with global state changes (e.g. initial load or post-save reload)
  useEffect(() => {
    setDoctors(globalDoctors);
  }, [globalDoctors]);

  const saveOne = useCallback(async (d: Doctor) => {
    setSaveStatus("saving");
    try {
      const apiData = {
        doctor_id: d.doctor_id || d.id,
        name: d.name,
        name_en: d.nameEn,
        specialty: d.specialty,
        specialty_en: d.specialtyEn,
        experience: d.experience,
        experience_en: d.experienceEn,
        image: d.image,
        rating: d.rating,
        reviews_count: d.reviewsCount,
        title: d.title,
        title_en: d.titleEn,
        education: d.education,
        education_en: d.educationEn,
        languages: d.languages,
        available_days: d.availableDays,
        bio: d.bio,
        bio_en: d.bioEn,
        specializations: d.specializations,
        achievements: d.achievements,
        consultation_fee: d.consultationFee,
        consultation_fee_en: d.consultationFeeEn,
        show_price: d.showPrice !== false,
        reviews: d.reviews,
      };
      
      // Update existing using its internal 'id' which matches backend UUID
      if (d.id && !d.id.startsWith("dr-") && d.id.length > 20) {
        await updateDoctor(d.id, apiData as CMSDoctorUpdate);
      } else {
        await createDoctor(apiData as CMSDoctorCreate);
      }
      
      await reload();
      setSaveStatus("saved");
      setHasChanges(false);
      setTimeout(() => setSaveStatus("idle"), 2500);
      return { success: true };
    } catch (err) {
      setSaveStatus("error");
      return { success: false, error: err };
    }
  }, [reload]);

  const remove = useCallback(async (id: string) => {
    setSaveStatus("saving");
    try {
      await apiDeleteDoctor(id);
      await reload();
      setSaveStatus("saved");
      setHasChanges(false);
      setTimeout(() => setSaveStatus("idle"), 2500);
      return { success: true };
    } catch (err) {
      setSaveStatus("error");
      return { success: false, error: err };
    }
  }, [reload]);

  const save = useCallback(async (data: Doctor[]) => {
    setSaveStatus("saving");
    try {
      for (const d of data) {
        const apiData = {
          doctor_id: d.doctor_id || d.id,
          name: d.name,
          name_en: d.nameEn,
          specialty: d.specialty,
          specialty_en: d.specialtyEn,
          experience: d.experience,
          experience_en: d.experienceEn,
          image: d.image,
          rating: d.rating,
          reviews_count: d.reviewsCount,
          title: d.title,
          title_en: d.titleEn,
          education: d.education,
          education_en: d.educationEn,
          languages: d.languages,
          available_days: d.availableDays,
          bio: d.bio,
          bio_en: d.bioEn,
          specializations: d.specializations,
          achievements: d.achievements,
          consultation_fee: d.consultationFee,
          consultation_fee_en: d.consultationFeeEn,
          show_price: d.showPrice !== false,
          reviews: d.reviews,
        };
        if (d.id && !d.id.startsWith("dr-") && d.id.length > 20) {
          await updateDoctor(d.id, apiData as CMSDoctorUpdate);
        } else {
          await createDoctor(apiData as CMSDoctorCreate);
        }
      }
      
      await reload();
      setSaveStatus("saved");
      setHasChanges(false);
      setTimeout(() => setSaveStatus("idle"), 2500);
      return { success: true };
    } catch (err) {
      setSaveStatus("error");
      return { success: false, error: err };
    }
  }, [reload]);

  const updateDoctors = useCallback((data: Doctor[]) => {
    setDoctors(data);
    setHasChanges(true);
  }, []);

  const reset = useCallback(async () => {
    await reload();
    setHasChanges(false);
  }, [reload]);

  return {
    doctors,
    loading,
    error,
    saveStatus,
    hasChanges,
    updateDoctors,
    save,
    saveOne,
    remove,
    reset,
    reload,
  };
}
